/**
 * Adaptive time formatting based on the time range
 *
 * Determines the appropriate time format based on the duration of the time range.
 * - Short ranges show more time detail (hours, minutes)
 * - Long ranges show less detail (just dates)
 */

export interface TimeRange {
  start: Date;
  end: Date;
}

/**
 * Check if a Date object is valid (not Invalid Date)
 */
function isValidDate(date: Date): boolean {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

/**
 * Safe date formatter that returns empty string for invalid dates
 */
function safeFormatDate(date: Date, formatter: Intl.DateTimeFormat): string {
  if (!isValidDate(date)) {
    return "";
  }
  return formatter.format(date);
}

/**
 * Get an adaptive time format function based on the time range
 *
 * Tooltips should always include date context, even for short ranges.
 *
 * @param range - The time range to format for
 * @returns A function that formats dates appropriately for the range
 */
export function getAdaptiveTimeFormat(range: TimeRange): (date: Date) => string {
  // Guard against invalid dates in range
  if (!isValidDate(range.start) || !isValidDate(range.end)) {
    return () => "";
  }

  // Tooltips show individual data points — always include time so users
  // can identify the exact moment.  Only the date detail level changes
  // with the range duration.

  const durationHours = (range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60);

  // Less than 30 days: month + day + time (e.g., "Oct 3, 3:00 PM")
  if (durationHours <= 720) {
    const formatter = new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    return (date: Date) => safeFormatDate(date, formatter);
  }

  // More than 30 days: month + day + time, add year when range spans years
  const startYear = range.start.getFullYear();
  const endYear = range.end.getFullYear();
  const showYear = startYear !== endYear;

  const formatter = new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(showYear && { year: "numeric" }),
  });
  return (date: Date) => safeFormatDate(date, formatter);
}

const DAY_MS = 86_400_000;

/**
 * Multi-scale tick label format (the d3 axis convention): each tick is labeled
 * at the coarsest calendar boundary it sits on — minutes within an hour, hours
 * within a day, the date at midnight, the month at the 1st, the year at Jan 1.
 * Adjacent ticks of a calendar-aligned tick set therefore never repeat a
 * label, unlike a single range-wide format which duplicates once the tick
 * spacing is finer than its granularity.
 *
 * @param stepMs - Spacing between adjacent ticks; picks the weekday form
 *   ("Mon 11") for day-level ticks vs month+day ("Mar 11") for week-level.
 * @param options.compact - Shorter forms for narrow charts (e.g. "3p", "3/11").
 */
export function getTickTimeFormat(stepMs: number, options?: { compact?: boolean }): (date: Date) => string {
  const compact = options?.compact ?? false;
  const useWeekday = stepMs < 5 * DAY_MS;

  const hourParts = (date: Date) => {
    const hour = date.getHours();
    const period = hour >= 12 ? "PM" : "AM";
    return { displayHour: hour % 12 || 12, period };
  };

  return (date: Date): string => {
    if (!isValidDate(date)) return "";

    const midnight =
      date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;

    if (midnight && date.getDate() === 1) {
      if (date.getMonth() === 0) return String(date.getFullYear());
      return date.toLocaleDateString("en", { month: "short" });
    }
    if (midnight) {
      const day = date.getDate();
      if (useWeekday) {
        const weekday = date.toLocaleDateString("en", { weekday: "short" });
        // A weekday initial is ambiguous (T is Tuesday and Thursday), so compact falls back to month/day.
        return compact ? `${date.getMonth() + 1}/${day}` : `${weekday} ${day}`;
      }
      return compact ? `${date.getMonth() + 1}/${day}` : `${date.toLocaleDateString("en", { month: "short" })} ${day}`;
    }

    const { displayHour, period } = hourParts(date);
    if (date.getMinutes() === 0 && date.getSeconds() === 0) {
      return compact ? `${displayHour}${period.charAt(0).toLowerCase()}` : `${displayHour} ${period}`;
    }
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return compact
      ? `${displayHour}:${minutes}${period.charAt(0).toLowerCase()}`
      : `${displayHour}:${minutes} ${period}`;
  };
}

/**
 * Get an adaptive time format function for axis labels (more compact)
 *
 * @param range - The time range to format for
 * @param options - Optional formatting options
 * @param options.compact - If true, use more compact formats (e.g., "1/15" instead of "Jan 15")
 * @returns A function that formats dates appropriately for axis labels
 */
export function getAdaptiveAxisTimeFormat(range: TimeRange, options?: { compact?: boolean }): (date: Date) => string {
  // Guard against invalid dates in range
  if (!isValidDate(range.start) || !isValidDate(range.end)) {
    return () => "";
  }

  const durationHours = (range.end.getTime() - range.start.getTime()) / (1000 * 60 * 60);
  const compact = options?.compact ?? false;

  // Helper to wrap compact formatters with date validation
  const wrapWithValidation = (fn: (date: Date) => string) => (date: Date) => {
    if (!isValidDate(date)) return "";
    return fn(date);
  };

  // Less than 1 day: show time only (e.g., "3:00 PM" or "3pm" compact)
  if (durationHours <= 24) {
    if (compact) {
      return wrapWithValidation((date: Date) => {
        const hour = date.getHours();
        const period = hour >= 12 ? "pm" : "am";
        const displayHour = hour % 12 || 12;
        return `${displayHour}${period}`;
      });
    }
    const formatter = new Intl.DateTimeFormat("en", {
      hour: "numeric",
      minute: "2-digit",
    });
    return (date: Date) => safeFormatDate(date, formatter);
  }

  // Less than 2 days: show day + time (e.g., "Oct 3, 3pm" or "10/3 3p" compact)
  if (durationHours <= 48) {
    if (compact) {
      return wrapWithValidation((date: Date) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hour = date.getHours();
        const period = hour >= 12 ? "p" : "a";
        const displayHour = hour % 12 || 12;
        return `${month}/${day} ${displayHour}${period}`;
      });
    }
    const formatter = new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
      hour: "numeric",
    });
    return (date: Date) => safeFormatDate(date, formatter);
  }

  // Less than 7 days: show day of week + day (e.g., "Mon 3", or "10/3" compact — a weekday initial is ambiguous)
  if (durationHours <= 168) {
    if (compact) {
      return wrapWithValidation((date: Date) => `${date.getMonth() + 1}/${date.getDate()}`);
    }
    const formatter = new Intl.DateTimeFormat("en", {
      weekday: "short",
      day: "numeric",
    });
    return (date: Date) => safeFormatDate(date, formatter);
  }

  // Less than 30 days: show month + day (e.g., "Oct 3" or "10/3" compact)
  if (durationHours <= 720) {
    if (compact) {
      return wrapWithValidation((date: Date) => {
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${month}/${day}`;
      });
    }
    const formatter = new Intl.DateTimeFormat("en", {
      month: "short",
      day: "numeric",
    });
    return (date: Date) => safeFormatDate(date, formatter);
  }

  // More than 30 days: show month + day + year if different years
  const startYear = range.start.getFullYear();
  const endYear = range.end.getFullYear();
  const showYear = startYear !== endYear;

  if (compact) {
    return wrapWithValidation((date: Date) => {
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = showYear ? `/${date.getFullYear().toString().slice(-2)}` : "";
      return `${month}/${day}${year}`;
    });
  }

  const formatter = new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    ...(showYear && { year: "numeric" }),
  });
  return (date: Date) => safeFormatDate(date, formatter);
}
